<template>
  <div
    class="frisbuy-popup-container"
    @click.self="() => onBlurHandler()"
    v-touch:swipe="(direction: TSwipe) => onSwipeHandler(direction)"
  >
    <div
      class="frisbuy-popup-container__close-button-wrapper"
      :class="{
        'frisbuy-popup-container__close-button-wrapper--colored': isScrolled,
      }"
      :style="{
        height: styles.popupMarginTop,
      }"
    >
      <slot name="closeButton">
        <button class="frisbuy-close-button" @click="$emit('close')">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M6.35355 5.64645C6.15829 5.45118 5.84171 5.45118 5.64645 5.64645C5.45118 5.84171 5.45118 6.15829 5.64645 6.35355L11.2929 12L5.64645 17.6464C5.45118 17.8417 5.45118 18.1583 5.64645 18.3536C5.84171 18.5488 6.15829 18.5488 6.35355 18.3536L12 12.7071L17.6464 18.3536C17.8417 18.5488 18.1583 18.5488 18.3536 18.3536C18.5488 18.1583 18.5488 17.8417 18.3536 17.6464L12.7071 12L18.3536 6.35355C18.5488 6.15829 18.5488 5.84171 18.3536 5.64645C18.1583 5.45118 17.8417 5.45118 17.6464 5.64645L12 11.2929L6.35355 5.64645Z"
              fill="#858F9A"
            />
          </svg>
        </button>
      </slot>
    </div>

    <div
      class="frisbuy-popup-container__content-wrapper"
      ref="contentWrapper"
      :style="{
        height: `calc(100dvh - ${styles.popupMarginTop})`,
      }"
      @scroll="(e) => scrollHandler(e)"
    >
      <slot name="content" v-bind="contentWrapperSlot"></slot>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "@/widgets/MarketplaceReviews/mixins.scss";
.frisbuy-popup-container {
  &,
  *,
  *::before,
  *::after {
    margin: 0;
    padding: 0;
    text-decoration: none;
    list-style: none;

    box-sizing: border-box;
  }

  display: block;
  position: fixed;
  inset: 0;

  width: 100dvw;
  height: 100dvh;

  background-color: rgba($color: #000000, $alpha: 0.8);

  overflow: hidden;

  z-index: 100;

  .frisbuy-popup-container__close-button-wrapper {
    display: flex;
    position: relative;
    max-width: 1540px;
    margin: 0 auto;

    transition: background-color 0.2s linear;

    &.frisbuy-popup-container__close-button-wrapper--colored {
      background-color: #ffffff;
    }

    .frisbuy-close-button {
      display: block;
      position: relative;
      margin: auto 24px auto auto;

      width: fit-content;
      height: fit-content;

      background: transparent;
      outline: none;
      border: none;
      cursor: pointer;

      border-radius: 4px;

      z-index: 1;

      svg {
        display: block;
      }
    }
  }

  .frisbuy-popup-container__content-wrapper {
    display: block;
    position: relative;
    max-width: 1540px;
    min-width: 230px;
    overflow: auto;

    background-color: #ffffff;

    margin: 0 auto;
  }
}

// media queries
@media screen and (max-width: $size-s) {
  .frisbuy-popup-container {
    .frisbuy-popup-container__close-button-wrapper {
      max-width: $size-xs;
      background-color: #ffffff;

      margin: 0 auto;

      .frisbuy-close-button {
        background-color: rgba(255, 255, 255, 0.8);

        margin-right: 18px;

        svg {
          mix-blend-mode: difference;

          path {
            fill: #ffffff;
          }
        }
      }
    }

    .frisbuy-popup-container__content-wrapper {
      max-width: $size-xs;
      height: 100dvh;

      overflow-y: auto;
    }
  }
}

.frisbuy-popup-container {
  container-type: inline-size;

  @container (max-width: 681px) {
    .frisbuy-popup-container__content-wrapper {
      width: 100dvw;
    }
  }

  @container (max-width: 390px) {
    .frisbuy-close-button {
      svg {
        path {
          fill: #858f9a;
        }
      }
    }

    .frisbuy-popup-container__content-wrapper {
      width: 100dvw;
      overflow-y: auto;
    }
  }
}
</style>

<script setup lang="ts">
import { defineEmits, onBeforeUnmount, onMounted, ref } from "vue";
import { TSwipe } from "../../globals";

const emit = defineEmits<{
  blur: [];
  close: [];
  swipe: [value: TSwipe];
  scroll: [
    {
      top: number;
      bottom: number;
    }
  ];
}>();

onMounted(() => {
  lockWindowScroll();
});

onBeforeUnmount(() => {
  unlockWindowScroll();
});

function onBlurHandler() {
  emit("blur");
}

function lockWindowScroll() {
  document.documentElement.style.overflow = "hidden";
}

function unlockWindowScroll() {
  document.documentElement.style.overflow = "";
}

function onSwipeHandler(direction: TSwipe) {
  emit("swipe", direction);
}

const contentWrapper = ref<HTMLElement | null>(null);
const contentWrapperSlot = {
  scrollToTop,
};
function scrollToTop() {
  contentWrapper.value?.scrollTo({
    top: 0,
    behavior: "smooth",
  });
}

const isScrolled = ref(false);
const styles = {
  popupMarginTop: "60px",
};

function scrollHandler(e: Event) {
  const target = e.target as HTMLElement;
  const scrollTop = target.scrollTop;
  const topBarHeight = Number(styles.popupMarginTop.split(/[^0-9]/)[0]);

  isScrolled.value = scrollTop > topBarHeight;

  // Scroll position event
  const scrollBottom = target.scrollHeight - (target.offsetHeight + scrollTop);
  emit("scroll", {
    top: scrollTop,
    bottom: scrollBottom,
  });
}
</script>
